import {
    Interval,
    formatISO,
    isAfter,
    isBefore,
    isSameDay,
    isValid,
    isWithinInterval,
} from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

export const NUMBER_DAYS_WEEK = 7;

/**
 *
 * @param {string[]} dates The list of sorted dates which constitute the weeks.
 * @param {Date|number} date a date of a week.
 * @returns {number} The date code index of the date 'date' in the 'dates' list.
 */
export function getDateCodeIndex(dates: string[], date: Date | number): number {
    const dateCode = formatISO(date, { representation: 'date' });
    const dateCodeIndex = dates.findIndex(dateString => dateString === dateCode);

    if (dateCodeIndex && dateCodeIndex > -1) {
        return dateCodeIndex;
    }

    return 0;
}

/**
 *
 * @param {string[]} dates The list of sorted dates which constitute the weeks.
 * @param {Date|number} date a date of a week.
 * @returns {number} The index of the first date of the week of the date 'date' in the 'dates' list.
 */
export function getFirstWeekDateIndex(dates: string[], date: Date | number): number {
    const dateCodeIndex = getDateCodeIndex(dates, date);
    const weekIndex = Math.floor(dateCodeIndex / NUMBER_DAYS_WEEK);
    return weekIndex * NUMBER_DAYS_WEEK;
}

export function getCurrentDate(): Date {
    return new Date();
}

export function toStartOfDate(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
}

export function toEndOfDate(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59);
    return newDate;
}

/**
 *
 * @param {Date} date The date to check.
 * @param {Interval} interval Interval of dates, with start and end.
 * @returns {boolean} true if the day of the date is inside the interval, false otherwise.
 * Note: this function only compares the days (hours, minutes, and lower subdivisions, are not taken into account)
 */
export const isBetweenDays = (day: Date, interval: Interval) => {
    const { start, end } = interval;

    if (!interval.start) return false;
    if (isSameDay(day, start)) return true;

    if (!interval.end) return false;
    return isSameDay(day, end) || (isAfter(day, start) && isBefore(day, end));
};

/**
 * Implementation based on https://stackoverflow.com/a/59667261/295783
 * @param {number} week the number corresponding to the a specific week in the year
 * @param {number} year the year number
 * @returns the date of the first day of the given week and year
 */
export const getISOWeekYearDate = (week: number, year: number, weekDay = 0) => {
    const date = new Date(year, 0, 4);
    const offset = (date.getDay() || 7) - weekDay;
    date.setDate(week * 7 - offset - 3);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
};

/**
 * @param {Number} week the week number
 * @param {Number} year the year number
 * @param {Object} dateRange the range of the week
 * @returns {Boolean} return true if the date of the week is in the range of the given date
 */
export const getIsWeekWithinDateRange = (
    week: number,
    year: number,
    dateRange: { start: Date; end: Date }
): boolean => {
    const dateOfTheWeek = getISOWeekYearDate(week, year);
    const { start, end } = dateRange;
    if (!isValid(start) || !isValid(end) || !week || !year) {
        return false;
    }

    return isWithinInterval(dateOfTheWeek, {
        start,
        end,
    });
};

/**
 * @param {Date} date the date to format
 * @returns {String} return the formatted date. Ex: "2013-03-10T02:00:00Z" => "2013-03-10"
 */
export const getISOFormattedDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

/**
 * @returns {string} The IANA timezone identifier of the user's browser (e.g. "Europe/Paris")
 */
export function getUserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Converts a date string (YYYY-MM-DD) and time string (HH:mm) expressed in a given IANA timezone
 * to a UTC Date object.
 *
 * Algorithm: treat the input as if it were UTC, then find the true timezone offset at that instant
 * via Intl.DateTimeFormat.formatToParts, and subtract the offset.
 *
 * @param {string} dateStr - Date in "YYYY-MM-DD" format
 * @param {string} timeStr - Time in "HH:mm" format
 * @param {string} timezone - IANA timezone identifier (e.g. "Europe/Paris")
 * @returns {Date} The corresponding UTC instant
 */
export function buildUTCFromUserTimezone(dateStr: string, timeStr: string, timezone: string): Date {
    return fromZonedTime(`${dateStr}T${timeStr}:00`, timezone);
}

/**
 * Extracts the time portion ("HH:mm") of a UTC Date, displayed in a given IANA timezone.
 *
 * @param {Date} utcDate - A UTC instant
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Time string in "HH:mm" format
 */
export function extractTimeFromUTCInTimezone(utcDate: Date, timezone: string): string {
    return formatInTimeZone(utcDate, timezone, 'HH:mm');
}

/**
 * Returns true if the ISO date string has a configured time component.
 * Filters out legacy promotions stored as "YYYY-MM-DDT00:00:00.000Z" (midnight UTC = no time configured).
 * New promotions without a time are sent as "YYYY-MM-DD" (no T), so the `includes('T')` check alone
 * is sufficient for them.
 */
export function hasTime(value: string, date: Date): boolean {
    return (
        value?.includes('T') &&
        isValid(date) &&
        (date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0)
    );
}
